// ==========================
// Version 8 — context/AuthContext.js
// - Ensures /users/{uid} exists via transaction
// - Keeps meta.email current; sets meta.createdAt only once
// - Exposes refreshCurrentUser for email verification state refreshes
// - Lets public routes render while auth performs its first check
// - Adds localhost-only mock auth for mobile UI testing when Firebase login is unavailable
// - Primes localhost mock auth before mock route jumps can remove query params
// ==========================
import React, { createContext, useCallback, useEffect, useState, useContext } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { db } from '../firebase';
import {
  doc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';

const AuthContext = createContext();
const LOCAL_MOCK_AUTH_KEY = 'bwak:local-mock-auth';

function isLocalDevelopmentHost() {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

function readLocalMockUser() {
  if (!isLocalDevelopmentHost()) return null;

  const params = new URLSearchParams(window.location.search);
  const requestedMode = params.get('bwak_mock_auth');

  if (requestedMode === 'off') {
    window.localStorage?.removeItem(LOCAL_MOCK_AUTH_KEY);
    return null;
  }

  if (requestedMode === 'admin' || requestedMode === 'user') {
    window.localStorage?.setItem(LOCAL_MOCK_AUTH_KEY, requestedMode);
  }

  const mode = window.localStorage?.getItem(LOCAL_MOCK_AUTH_KEY);
  if (mode !== 'admin' && mode !== 'user') return null;

  return {
    uid: `local-${mode}-preview`,
    email: mode === 'admin'
      ? 'local.admin@blessedwithakiss.test'
      : 'local.user@blessedwithakiss.test',
    displayName: mode === 'admin' ? 'Local Admin Preview' : 'Local User Preview',
    photoURL: null,
    emailVerified: true,
    providerData: [{ providerId: 'google.com' }],
    bwakLocalMock: true,
    bwakLocalMockAdmin: mode === 'admin',
    reload: async () => {},
  };
}

function primeLocalMockAuthFromUrl() {
  if (!isLocalDevelopmentHost()) return;

  const params = new URLSearchParams(window.location.search);
  const requestedMode = params.get('bwak_mock_auth');

  if (requestedMode === 'off') {
    window.localStorage?.removeItem(LOCAL_MOCK_AUTH_KEY);
  } else if (requestedMode === 'admin' || requestedMode === 'user') {
    window.localStorage?.setItem(LOCAL_MOCK_AUTH_KEY, requestedMode);
  }
}

async function ensureUserMeta(user) {
  if (!user?.uid) return;

  const ref = doc(db, 'users', user.uid);

  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);

      const base = {
        meta: {
          email: user.email || null,
        },
      };

      if (!snap.exists()) {
        // First time we see this user – set both email + createdAt
        base.meta.createdAt = serverTimestamp();
        tx.set(ref, base, { merge: true });
        // log inside tx is fine just for visibility
        // (won't show until commit succeeds)
      } else {
        // Doc exists: keep email current; only set createdAt if truly missing
        const hasCreated = !!snap?.data()?.meta?.createdAt;
        if (!hasCreated) {
          base.meta.createdAt = serverTimestamp();
        }
        tx.set(ref, base, { merge: true });
      }
    });
    // eslint-disable-next-line no-console
    console.log('[AuthContext] ensureUserMeta: upserted /users/%s', user.uid);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[AuthContext] ensureUserMeta failed:', e);
  }
}

export const AuthProvider = ({ children }) => {
  primeLocalMockAuthFromUrl();

  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userVersion, setUserVersion] = useState(0);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      const localMockUser = !user ? readLocalMockUser() : null;
      setCurrentUser(user || localMockUser || null);
      setLoading(false);
      if (user) {
        await ensureUserMeta(user);
      }
    });
    return () => unsub();
  }, []);

  const logout = useCallback(async () => {
    if (currentUser?.bwakLocalMock) {
      window.localStorage?.removeItem(LOCAL_MOCK_AUTH_KEY);
      setCurrentUser(null);
      return;
    }
    await signOut(auth);
  }, [currentUser?.bwakLocalMock]);

  const refreshCurrentUser = useCallback(async () => {
    if (currentUser?.bwakLocalMock) return currentUser;
    if (!auth.currentUser) return null;
    await auth.currentUser.reload();
    setCurrentUser(auth.currentUser);
    setUserVersion((value) => value + 1);
    return auth.currentUser;
  }, [currentUser]);

  return (
    <AuthContext.Provider value={{ currentUser, loading, logout, refreshCurrentUser, userVersion }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
// ==========================
// End Version 8 — context/AuthContext.js
// ==========================
